/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useMemo } from 'react';
import { Grid } from '@mui/material';
import { useGetClassesOfRiskQuery } from '@/features/classesOfRisk/classesOfRiskApi';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useRiskClassSelectContext } from '@/context/riskClassSelectContext';
import FolioCheckbox from '../FolioCheckbox';

const RiskClassSelect = ({ disabled, clientType }) => {
	const { selectedItems, setSelectedItems } = useRiskClassSelectContext();
	const classesOfRiskKeys = selectedItems.map((i) => i.classOfRisk);

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		}
	);

	const countryCode = userDetails?.broker?.country ?? 'NZ';
	const brokerGroupIds = userDetails?.brokerGroupArray;

	const { data: classesOfRisk, ...classesOfRiskQuery } = useGetClassesOfRiskQuery(
		{ countryCode, clientType, brokerGroupIds },
		{ skip: userDetails.isLoading || userDetails.isUninitialized }
	);
	const children = (classesOfRisk ?? []).filter((i) => !i.isParent);

	const sortedClassesOfRisk = useMemo(() => {
		if (!classesOfRisk) return [];

		const classOfRiskKeys = Object.fromEntries(classesOfRisk.map((i) => [i.classOfRisk, i]));

		const dependencyGroups = [];
		const usedKeys = new Set();

		classesOfRisk.forEach((item) => {
			const conditions = item.conditions;
			if (!conditions) return;

			const dependencies = [...(conditions.and ?? []), ...(conditions.or ?? [])];
			if (dependencies.length === 0) return;

			const bases = dependencies.map((key) => classOfRiskKeys[key]).filter(Boolean);

			const group = [...bases, item];
			group.forEach((i) => usedKeys.add(i.classOfRisk));
			dependencyGroups.push(group);
		});

		const ordered = Array.from(new Set(dependencyGroups.flat()));
		const remaining = classesOfRisk
			.filter((i) => !usedKeys.has(i.classOfRisk))
			.sort((a, b) => (a.order ?? 999) - (b.order ?? 999)); // Sort by order field

		const combined = [...ordered, ...remaining];

		return combined.filter((item) => {
			const conditions = item.conditions ?? {};
			const andConditions = conditions.and ?? [];
			const orConditions = conditions.or ?? [];

			const hasAllAndConditions =
				andConditions.length === 0 ||
				andConditions.every((c) => classesOfRiskKeys.includes(c));

			const hasAnyOrCondition =
				orConditions.length === 0 ||
				orConditions.some((c) => classesOfRiskKeys.includes(c));

			return hasAllAndConditions && hasAnyOrCondition;
		}).sort((a, b) => (a.order ?? 999) - (b.order ?? 999)); // Final sort by order
	}, [classesOfRisk, classesOfRiskKeys]);

	return (
		<Grid container rowSpacing={1} columnSpacing={2}>
			{sortedClassesOfRisk.map((item, i) => {
				const checked = selectedItems.findIndex((i) => i.id === item.id) > -1;
				return (
					<Grid
						size={{ xs: 12, sm: 6, md: 4 }}
						key={`risk-classes-${item.classOfRisk}-${i}`}
					>
						<FolioCheckbox
							checked={checked}
							label={item.label}
							onChange={() => {
								const newValues = checked
									? selectedItems.filter((i) => i.id !== item.id)
									: [...selectedItems, item];
								setSelectedItems(newValues);
							}}
							disabled={disabled}
						/>
					</Grid>
				);
			})}
		</Grid>
	);
};

export default RiskClassSelect;
